/**
 * 递归去除对象 / 数组中每个字符串值的前后空格。
 * - 保留字符串中间的空格；
 * - 不动非字符串原始值（number / boolean / null / undefined）；
 * - 跳过二进制与特殊结构（FormData / Blob / File / URLSearchParams / Date），避免破坏或报错。
 *
 * 用于请求拦截器：发起请求前对 params / data 各跑一遍，自动清理用户输入的的前后空格。
 */
/**
 * 递归深度上限：仅用于防御异常深或自引用的结构（自引用若不拦截会爆栈）。
 */
const MAX_TRIM_DEPTH = 32;

export function deepTrim<T>(value: T): T {
  return trimNode(value, 0, new WeakSet<object>()) as T;
}

function trimNode(value: any, depth: number, seen: WeakSet<object>): any {
  if (value == null) return value;
  // 二进制 / 表单 / 查询串：原样返回，不碰
  if (
    typeof FormData !== 'undefined' &&
    value instanceof FormData
  ) {
    return value;
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
  if (typeof File !== 'undefined' && value instanceof File) return value;
  if (
    typeof URLSearchParams !== 'undefined' &&
    value instanceof URLSearchParams
  ) {
    return value;
  }
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === value ? value : trimmed;
  }
  if (typeof value !== 'object') return value; // number / boolean 等
  // 超过深度上限或已在当前路径上出现过（自引用）→ 原样返回，保证不爆栈
  if (depth >= MAX_TRIM_DEPTH || seen.has(value)) return value;

  seen.add(value);
  try {
    if (Array.isArray(value)) {
      let changed = false;
      const out = value.map((item) => {
        const next = trimNode(item, depth + 1, seen);
        if (next !== item) changed = true;
        return next;
      });
      // 没有任何元素被改动时复用原数组引用，省掉一次无意义的重建
      return changed ? out : value;
    }

    const proto = Object.getPrototypeOf(value);
    const isPlain = proto === Object.prototype || proto === null;
    let changed = false;
    const out: Record<string, unknown> = {};
    const source = value as Record<string, unknown>;
    for (const k of Object.keys(source)) {
      const next = trimNode(source[k], depth + 1, seen);
      if (next !== source[k]) changed = true;
      out[k] = next;
    }
    // 纯对象且整棵子树无改动 → 复用原引用；非纯对象（类实例）仍展平成普通对象。
    if (isPlain && !changed) return value;
    return out;
  } finally {
    seen.delete(value);
  }
}
